import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from '../config';
import { useIsMobile } from '../hooks/useMediaQuery';

// User Browse (SHARC_Spec_User_Browse_2026-10-28) — Collaborate → Users.
// Rebuilt on MemberList.js (Member Browse): one search line, sortable
// columns, mobile cards + sort row, and jump-to-page pagination.
//
// Only public users are ever listed — GET /users (getUsers.js) filters to
// is_public = TRUE + username set + onboarding completed server-side, so a
// private user's data never reaches the browser. No total count and no
// "+ Add" button by decision (spec D6).
//
// Total Shows / Most Recent Show come from the same calculation as
// My Shows → Followers → Following (followStats.js computeShowStats).

// Normalize for search: strip diacritics and punctuation so "motorhead"
// matches "Motörhead" and "carissas" matches "Carissa's Wierd".
const normalizeStr = (str) =>
    (str || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/['‘’‛`\-.,:!?&]/g, '')
        .toLowerCase();

const PAGE_SIZE = 25;

const NUMERIC_COLS = new Set(['total_shows']);

// Shared between the desktop table headers and the mobile "Sort by" select,
// so both stay in sync automatically if columns ever change.
// name_text is derived: display name, falling back to username (spec D3).
const SORT_COLUMNS = [
    { col: 'name_text',        label: 'User Display Name' },
    { col: 'member_name',      label: 'Member Name' },
    { col: 'city',             label: 'City' },
    { col: 'state',            label: 'State' },
    { col: 'country',          label: 'Country' },
    { col: 'total_shows',      label: 'Total Shows' },
    { col: 'most_recent_show', label: 'Most Recent Show' },
];

const plural = (n, word) => `${n} ${n === 1 ? word : `${word}s`}`;

// Same format as FollowersFollowingTab.js ("Oct 12, 2026", UTC).
function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

// The viewer's own row goes to My Shows (spec D2); everyone else to their
// public profile.
const profilePath = (u) => (u.is_self ? '/my-shows' : `/u/${u.username}`);

const isBlank = (v) => v === null || v === undefined || String(v).trim() === '';

// ── Member link (shared by table cell + mobile card) ───────────────────────
function MemberLink({ user }) {
    if (!user.member_name) return <span style={S.dash}>—</span>;
    return (
        <Link to={`/members/${user.member_slug || user.member_id}`} style={S.link}>{user.member_name}</Link>
    );
}

// ── Jump-to-page pagination (local copy of MemberList.js's JumpPagination —
// copy-per-file convention, see context doc Section 12) ────────────────────
function JumpPagination({ currentPage, totalItems, onPageChange, pageSize = PAGE_SIZE }) {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    if (totalPages <= 1) return null;
    return (
        <div style={S.pageJumpWrap}>
            <span style={S.pageJumpLabel}>Page:</span>
            <select
                value={currentPage}
                onChange={e => onPageChange(Number(e.target.value))}
                style={S.pageJumpSelect}
            >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <option key={p} value={p}>{p}</option>
                ))}
            </select>
            <span style={S.pageJumpLabel}>of {totalPages}</span>
        </div>
    );
}

// ── Mobile card (replaces the table below 768px) ───────────────────────────
// A plain container rather than one big <Link>: it can hold both a profile
// link and a member link, and links can't be nested.
function UserCardMobile({ user }) {
    const location = [user.city, user.state, user.country].filter(Boolean).join(', ');
    const showCount = Number(user.total_shows) || 0;
    return (
        <div style={mobileStyles.card}>
            <Link to={profilePath(user)} style={mobileStyles.cardName}>{user.name_text}</Link>
            {location && <div style={mobileStyles.cardLocation}>{location}</div>}
            {user.member_name && (
                <div style={mobileStyles.cardMember}>
                    <span style={mobileStyles.cardMemberLabel}>Member: </span>
                    <MemberLink user={user} />
                </div>
            )}
            <div style={mobileStyles.cardStatsRow}>
                <span>{plural(showCount, 'show')} · Most Recent Show: {formatDate(user.most_recent_show)}</span>
            </div>
        </div>
    );
}

function UserList() {
    const isMobile = useIsMobile();
    const [users, setUsers]     = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    // Filter state — single search line only
    const [search, setSearch] = useState('');

    // Sort state — default User Display Name A–Z (spec D4)
    const [sortCol, setSortCol] = useState('name_text');
    const [sortDir, setSortDir] = useState('asc');
    const [page, setPage]       = useState(1);

    useEffect(() => {
        document.title = 'SHARC - Users';
        let cancelled = false;
        (async () => {
            try {
                const { tokens } = await fetchAuthSession();
                const token = tokens.idToken.toString();
                const res = await axios.get(`${config.API_URL}/users`, { headers: { Authorization: token } });
                if (cancelled) return;
                // Derive name_text (display name → username fallback) and a
                // pre-normalized search haystack once, rather than per keystroke.
                const rows = (res.data.users || []).map(u => {
                    const nameText = (u.display_name && u.display_name.trim()) || u.username;
                    return {
                        ...u,
                        name_text: nameText,
                        search_text: normalizeStr(
                            [nameText, u.username, u.member_name, u.city, u.state, u.country]
                                .filter(Boolean).join(' ')
                        ),
                    };
                });
                setUsers(rows);
                setLoading(false);
            } catch {
                if (cancelled) return;
                setError('Failed to load users');
                setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            document.title = 'SHARC - Shows Archive';
        };
    }, []);

    const handleSort = (col) => {
        if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(col); setSortDir('asc'); }
        setPage(1);
    };

    // Matches user display name, username, member name, city, state or
    // country. Multi-word searches must match every word ("tampa fl").
    const filtered = useMemo(() => {
        const words = normalizeStr(search).split(/\s+/).filter(Boolean);
        if (words.length === 0) return users;
        return users.filter(u => words.every(w => u.search_text.includes(w)));
    }, [users, search]);

    // Blank values (no member, no city, no shows yet) always sort last,
    // whichever direction is selected. Ties fall back to name A–Z.
    const sorted = useMemo(() => {
        const dir = sortDir === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => {
            const av = a[sortCol];
            const bv = b[sortCol];
            const aBlank = NUMERIC_COLS.has(sortCol) ? false : isBlank(av);
            const bBlank = NUMERIC_COLS.has(sortCol) ? false : isBlank(bv);
            let cmp;
            if (aBlank && bBlank) cmp = 0;
            else if (aBlank) return 1;
            else if (bBlank) return -1;
            else if (NUMERIC_COLS.has(sortCol)) cmp = (Number(av) || 0) - (Number(bv) || 0);
            else if (sortCol === 'most_recent_show') cmp = new Date(av) - new Date(bv);
            else cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
            if (cmp !== 0) return cmp * dir;
            return String(a.name_text).localeCompare(String(b.name_text), undefined, { sensitivity: 'base' });
        });
    }, [filtered, sortCol, sortDir]);

    const clearFilters = () => {
        setSearch('');
        setPage(1);
    };

    const hasFilters = !!search;

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <span style={S.sortIcon}>↕</span>;
        return <span style={{ ...S.sortIcon, color: '#bb86fc' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
    };

    if (loading) return <div style={S.message}>Loading users...</div>;
    if (error)   return <div style={S.errMsg}>{error}</div>;

    const pageItems = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            {/* ── Header — title only: no count, no + Add (spec D6) ── */}
            <div style={S.header}>
                <h1 style={S.title}>Users</h1>
            </div>

            {/* ── Search (single line, no dropdowns) ── */}
            <div style={S.filterBar}>
                <input
                    type="text"
                    placeholder="Search by user name, member name, city, state, country..."
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                    style={S.searchInput}
                    autoComplete="off"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                />
                {hasFilters && (
                    <button onClick={clearFilters} style={S.clearBtn}>✕ Clear all</button>
                )}
            </div>

            {/* ── Mobile-only sort control (table headers below handle this on desktop) ── */}
            {isMobile && (
                <div style={mobileStyles.sortRow}>
                    <select
                        value={sortCol}
                        onChange={e => { setSortCol(e.target.value); setPage(1); }}
                        style={mobileStyles.sortSelect}
                    >
                        {SORT_COLUMNS.map(({ col, label }) => (
                            <option key={col} value={col}>Sort: {label}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                        style={mobileStyles.sortDirBtn}
                        aria-label={sortDir === 'asc' ? 'Ascending' : 'Descending'}
                    >
                        {sortDir === 'asc' ? '↑ Asc' : '↓ Desc'}
                    </button>
                </div>
            )}

            {sorted.length === 0 ? (
                <div style={S.message}>
                    {hasFilters ? 'No users match your search.' : 'No public users yet.'}
                </div>
            ) : isMobile ? (
                <div style={mobileStyles.cardList}>
                    {pageItems.map((u) => (
                        <UserCardMobile key={u.username} user={u} />
                    ))}
                </div>
            ) : (
                <div style={S.tableContainer}>
                    <table style={S.table}>
                        <thead>
                            <tr>
                                {SORT_COLUMNS.map(({ col, label }) => (
                                    <th
                                        key={col}
                                        style={{ ...S.th, ...(NUMERIC_COLS.has(col) ? S.thNum : {}), cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort(col)}
                                    >
                                        {label} <SortIcon col={col} />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pageItems.map((u, idx) => (
                                <tr
                                    key={u.username}
                                    style={idx % 2 === 0 ? S.trEven : S.trOdd}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#252525'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#1e1e1e' : '#1a1a1a'}
                                >
                                    <td style={S.td}>
                                        <Link to={profilePath(u)} style={S.link}>{u.name_text}</Link>
                                    </td>
                                    <td style={S.td}><MemberLink user={u} /></td>
                                    <td style={S.td}>{u.city || '—'}</td>
                                    <td style={S.td}>{u.state || '—'}</td>
                                    <td style={S.td}>{u.country || '—'}</td>
                                    <td style={{ ...S.td, ...S.numCell }}>{Number(u.total_shows) || 0}</td>
                                    <td style={S.td}>{formatDate(u.most_recent_show)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <JumpPagination currentPage={page} totalItems={sorted.length} onPageChange={setPage} />
        </div>
    );
}

const S = {
    header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    title:       { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', margin: 0 },

    filterBar:    { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'center' },
    searchInput:  { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '9px 14px', fontSize: '14px', color: '#e0e0e0', flex: '1 1 260px', minWidth: '200px', boxSizing: 'border-box' },
    clearBtn:     { backgroundColor: 'transparent', color: '#888', border: '1px solid #444', borderRadius: '6px', padding: '9px 12px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },

    tableContainer: { overflowX: 'auto' },
    table:          { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th:             { textAlign: 'left', padding: '10px 14px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    thNum:          { textAlign: 'right' },
    td:             { padding: '10px 14px', color: '#e0e0e0', borderBottom: '1px solid #222', whiteSpace: 'nowrap' },
    trEven:         { backgroundColor: '#1e1e1e' },
    trOdd:          { backgroundColor: '#1a1a1a' },
    link:           { color: '#bb86fc', textDecoration: 'none' },
    dash:           { color: '#e0e0e0' },
    numCell:        { textAlign: 'right', color: '#aaa', fontVariantNumeric: 'tabular-nums' },
    sortIcon:       { fontSize: '10px', marginLeft: '4px', color: '#555' },
    message:        { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    errMsg:         { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },

    // Jump-to-page pagination — copied from MemberList.js
    pageJumpWrap:   { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' },
    pageJumpLabel:  { fontSize: '13px', color: '#aaa', fontWeight: '600' },
    pageJumpSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 10px', fontSize: '14px', color: '#e0e0e0', cursor: 'pointer', fontWeight: 'bold', width: '64px', flexShrink: 0, textAlign: 'center', textAlignLast: 'center' },
};

// Kept as a separate object, only used when useIsMobile() is true, so the
// desktop table above is completely untouched.
const mobileStyles = {
    sortRow: {
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
    },
    sortSelect: {
        flex: '1 1 auto',
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '6px',
        padding: '9px 12px',
        fontSize: '13px',
        color: '#e0e0e0',
        boxSizing: 'border-box',
    },
    sortDirBtn: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '6px',
        padding: '9px 14px',
        fontSize: '13px',
        color: '#e0e0e0',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    cardList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    card: {
        display: 'block',
        backgroundColor: '#1e1e1e',
        border: '1px solid #2e2e2e',
        borderRadius: '8px',
        padding: '14px 16px',
        color: '#e0e0e0',
    },
    cardName: {
        display: 'inline-block',
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#bb86fc',
        textDecoration: 'none',
        marginBottom: '4px',
    },
    cardLocation: {
        fontSize: '13px',
        color: '#ccc',
        marginBottom: '8px',
    },
    cardMember: {
        fontSize: '13px',
        lineHeight: 1.5,
        marginBottom: '8px',
    },
    cardMemberLabel: {
        color: '#aaa',
    },
    cardStatsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px 12px',
        fontSize: '12px',
        color: '#aaa',
        paddingTop: '8px',
        borderTop: '1px solid #2a2a2a',
    },
};

export default UserList;
